import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { apiClient, CrashReport } from '../api/client';
import { AppBadge, AppFilterSelect, AppFilterValue } from '../components/AppFlavor';

export default function CrashList() {
  const [crashes, setCrashes] = useState<CrashReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  // Filters
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [platform, setPlatform] = useState('');
  const [errorType, setErrorType] = useState('');
  const [app, setApp] = useState<AppFilterValue>('');

  useEffect(() => {
    async function fetchCrashes() {
      setLoading(true);
      try {
        const data = await apiClient.getCrashes({
          search: search || undefined,
          status: status || undefined,
          platform: platform || undefined,
          error_type: errorType || undefined,
          app: app || undefined,
          page,
          limit: 20,
        });
        setCrashes(data.crashes);
        setTotal(data.total);
        setPages(data.pages);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load crashes');
      } finally {
        setLoading(false);
      }
    }

    const debounce = setTimeout(fetchCrashes, 300);
    return () => clearTimeout(debounce);
  }, [search, status, platform, errorType, app, page]);

  const getStatusBadgeClass = (crashStatus: string) => {
    switch (crashStatus) {
      case 'new':
        return 'badge-error';
      case 'investigating':
        return 'badge-warning';
      case 'resolved':
        return 'badge-success';
      case 'ignored':
        return 'badge-neutral';
      default:
        return 'badge-neutral';
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <AlertTriangle className="text-red-500" />
          Crashes
        </h1>
        <span className="text-sm text-gray-500">{total} total crashes</span>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex gap-4 items-center flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search error message..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Status</option>
            <option value="new">New</option>
            <option value="investigating">Investigating</option>
            <option value="resolved">Resolved</option>
            <option value="ignored">Ignored</option>
          </select>

          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Platforms</option>
            <option value="ios">iOS</option>
            <option value="android">Android</option>
          </select>

          <select
            value={errorType}
            onChange={(e) => setErrorType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Types</option>
            <option value="crash">Crash</option>
            <option value="unhandled_rejection">Unhandled Promise</option>
            <option value="error_boundary">Error Boundary</option>
            <option value="native">Native</option>
          </select>

          <AppFilterSelect
            value={app}
            onChange={(value) => {
              setApp(value);
              setPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {/* Crash list */}
      {loading ? (
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      ) : crashes.length === 0 ? (
        <div className="card text-center py-12">
          <AlertTriangle size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No crashes found</p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {crashes.map((crash) => (
              <Link
                key={crash.id}
                to={`/crashes/${crash.id}`}
                className="card block hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`badge ${getStatusBadgeClass(crash.status)}`}>
                        {crash.status}
                      </span>
                      <span className="badge badge-neutral">{crash.error_type}</span>
                      <span
                        className={`badge ${
                          crash.platform === 'ios' ? 'badge-info' : 'badge-success'
                        }`}
                      >
                        {crash.platform === 'ios' ? 'iOS' : 'Android'}
                      </span>
                      <AppBadge flavor={crash.app_flavor} />
                      <span className="text-sm text-gray-500">v{crash.app_version}</span>
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 mb-1">
                      {crash.error_name || 'Error'}
                    </h3>
                    <p className="text-sm text-gray-600 truncate">{crash.error_message}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>Device: {crash.device_id.slice(0, 8)}...</span>
                      {crash.screen_name && <span>Screen: {crash.screen_name}</span>}
                      {crash.device_model && <span>Model: {crash.device_model}</span>}
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-500 whitespace-nowrap ml-4">
                    {formatDistanceToNow(new Date(crash.occurred_at), { addSuffix: true })}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-6">
            <span className="text-sm text-gray-500">
              Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, total)} of {total} crashes
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm text-gray-600">
                Page {page} of {pages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={page === pages}
                className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
